import { DOMINIO_INTERNO, supabase } from '@/shared/lib/supabase';
import { ErrorDeBase } from '@/shared/lib/errores';
import { esperar, llamarRpc } from '@/shared/lib/rpc';

export const AREAS = [
  'MAESTROS',
  'CUENTAS',
  'STOCK',
  'DOCUMENTOS',
  'ACOPIO',
  'TRANSPORTE',
  'ADMIN',
  'SEGURIDAD',
] as const;

export type Area = (typeof AREAS)[number];

export interface PermisoDeArea {
  leer: boolean;
  escribir: boolean;
}

export interface Sesion {
  /** Id de perfil (`sec.users.id`). */
  perfilId: number;
  usuario: string;
  /** Nombre del rol; `null` si el rol no es legible para este usuario. */
  rol: string | null;
  /** `true` si el administrador marco que debe cambiar la clave. */
  debeCambiarClave: boolean;
  permisos: Record<Area, PermisoDeArea>;
}

/** Supabase Auth solo entiende mails: se agrega el dominio interno al usuario. */
function mailInterno(usuario: string): string {
  return usuario.trim().toLowerCase() + DOMINIO_INTERNO;
}

export async function iniciarSesion(usuario: string, contrasena: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({
    email: mailInterno(usuario),
    password: contrasena,
  });
  if (error) {
    throw new ErrorDeBase(
      error.status === 400 ? 'Usuario o contraseña incorrectos.' : error.message,
      error.code,
    );
  }
}

export async function cerrarSesion(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw new ErrorDeBase(error.message, error.code);
}

interface FilaDeRol {
  roles: { name: string; role_permissions: { area: Area; can_write: boolean }[] } | null;
}

/**
 * Lee la sesion activa y arma el perfil. Devuelve `null` si no hay login.
 *
 * Los permisos salen de UNA sola consulta (el rol del usuario con sus filas de `sec.role_permissions`):
 * antes eran dos llamadas por cada una de las 8 areas y la pantalla tardaba segundos en abrir.
 * Sin fila de un area no hay acceso; con `can_write` falso es solo lectura (lo mismo que `sec.has_area`).
 *
 * El nombre del rol y la marca de cambio de clave viven en tablas que solo ve SEGURIDAD
 * (RLS): para el resto de los usuarios se degradan a un valor neutro en vez de fallar.
 */
export async function obtenerSesion(): Promise<Sesion | null> {
  const { data } = await supabase.auth.getSession();
  if (!data.session) return null;

  const perfilId = await llamarRpc<number>('sec', 'current_user_id');
  const usuario = (data.session.user.email ?? '').replace(DOMINIO_INTERNO, '');

  const [perfil, fila] = await Promise.all([
    esperar<{ must_reset_password: boolean } | null>(
      supabase
        .schema('sec')
        .from('users')
        .select('must_reset_password')
        .eq('id', perfilId)
        .maybeSingle(),
    ).catch(() => null),
    esperar<FilaDeRol | null>(
      supabase
        .schema('sec')
        .from('user_roles')
        .select('roles(name, role_permissions(area, can_write))')
        .eq('user_id', perfilId)
        .maybeSingle(),
    ).catch(() => null),
  ]);

  const filasDePermiso = fila?.roles?.role_permissions ?? [];
  const permisos = Object.fromEntries(
    AREAS.map((area) => {
      const permiso = filasDePermiso.find((candidata) => candidata.area === area);
      return [area, { leer: permiso !== undefined, escribir: permiso?.can_write === true }];
    }),
  ) as Record<Area, PermisoDeArea>;

  return {
    perfilId,
    usuario,
    rol: fila?.roles?.name ?? null,
    debeCambiarClave: perfil?.must_reset_password ?? false,
    permisos,
  };
}

/** Cambia la clave propia con `sec.set_user_password`. */
export function cambiarClave(perfilId: number, contrasena: string): Promise<null> {
  return llamarRpc<null>('sec', 'set_user_password', {
    p_user: perfilId,
    p_password: contrasena,
  });
}
